import axios from 'axios';
import { API_CONFIG } from '../config/api';

// Timeout padrão da instância axios
const DEFAULT_TIMEOUT = API_CONFIG.TIMEOUT;

// Configurar axios com URL base
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: DEFAULT_TIMEOUT,
});

const PUBLIC_FRONTEND_ROUTE_PATTERNS = [
  /^\/article\/[^/]+/,
  /^\/print\/[^/]+/,
  /^\/test\/[^/]+/,
  /^\/dashboard(\/|$)/,
  /^\/d\/[^/]+/,
  /^\/public\/dashboard\/[^/]+/
];

const isPublicFrontendRoute = () => {
  if (typeof window === 'undefined') return false;
  return PUBLIC_FRONTEND_ROUTE_PATTERNS.some(pattern => pattern.test(window.location.pathname));
};

// Interceptor para adicionar token e brandId automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Multi-tenant: adicionar brandId se disponivel
    const brandId = localStorage.getItem('activeBrandId');
    if (brandId && brandId !== 'undefined' && brandId !== 'null') {
      config.headers['x-brand-id'] = brandId;
    }

    // Deixe o Axios gerenciar o Content-Type automaticamente (muito mais estável)
    // Ele define application/json para objetos e multipart/form-data para FormData

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com respostas
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const skipAuthRedirect = error.config?.skipAuthRedirect || isPublicFrontendRoute();
      const isPublicDashboardPinChallenge =
        error.response?.data?.code === 'PIN_REQUIRED' &&
        error.config?.url?.includes('/public/dashboard/');

      if (isPublicDashboardPinChallenge) {
        return Promise.reject(error);
      }

      // Token expirado ou inválido (evitar loop infinito se já estiver na tela de login)
      if (skipAuthRedirect || window.location.pathname === '/login') {
        return Promise.reject(error);
      }

      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    // Limite mensal de publicações atingido: abrir dialog de upsell
    if (error.response?.status === 429) {
      const detail = error.response?.data || {};
      // Verificar se NÃO é um rate limit genérico do express (proteção contra força bruta/DDoS)
      const isGenericRateLimit = detail.message && 
        (detail.message.includes('Muitas tentativas') || detail.message.includes('Muitas requisições'));
      
      if (!isGenericRateLimit) {
        window.dispatchEvent(
          new CustomEvent('plan-limit-reached', { detail })
        );
      }
    }

    return Promise.reject(error);
  }
);

// Funções específicas para Tags
export const getTags = () => api.get('/tags');
export const createTag = (data) => api.post('/tags', data);
export const updateTag = (id, data) => api.put(`/tags/${id}`, data);
export const deleteTag = (id) => api.delete(`/tags/${id}`);

// Funções específicas para Artigos
export const getArticles = (params) => api.get('/articles', { params });
export const getArticle = (id) => api.get(`/articles/${id}`);
export const createArticle = (data) => api.post('/articles', data);
export const updateArticle = (id, data) => api.put(`/articles/${id}`, data);
export const deleteArticle = (id) => api.delete(`/articles/${id}`);
export const bulkDeleteArticles = (ids) => api.post('/articles/bulk-delete', { ids });
export const bulkRescrapeArticles = (ids) => api.post('/articles/bulk-rescrape', { ids });
export const generateArticlePDF = (id) => api.post(`/articles/${id}/generate-pdf`);
export const smartScrapeUrl = (url) => api.post('/articles/scrape-smart', { url }, { timeout: 120000 });
export const rescrapeSmartArticle = (id) => api.post(`/articles/${id}/rescrape-smart`, {}, { timeout: 120000 });
export const bulkAnalyzeKpia = (ids) => api.post('/articles/bulk-kpia', { articleIds: ids }, { timeout: 1800000 });
export const bulkNativeReviewArticles = (ids, tasks) =>
  api.post('/articles/bulk-native-review', { articleIds: ids, tasks }, { timeout: 300000 });

// Funções específicas para Domínios
export const getDomains = (params) => api.get('/domain-info', { params });
export const getDomain = (id) => api.get(`/domain-info/${id}`);
export const createDomain = (data) => api.post('/domain-info', data);
export const updateDomain = (id, data) => api.put(`/domain-info/${id}`, data);
export const deleteDomain = (id) => api.delete(`/domain-info/${id}`);

// Funções específicas para Domínios Pendentes
export const getPendingDomains = (params) => api.get('/pending-domains', { params });
export const processPendingDomain = (id, data) => api.post(`/pending-domains/${id}/process`, data);
export const fetchDomainData = (id) => api.post(`/pending-domains/${id}/fetch-data`);
export const ignorePendingDomain = (id, data) => api.post(`/pending-domains/${id}/ignore`, data);
export const syncDomainsFromArticles = () => api.post('/pending-domains/sync', {}, { timeout: API_CONFIG.LONG_TIMEOUT });
// Exporta links com suporte a filtros (missing, outdated, all, pending, selected) e formato (json, excel)
export const exportPendingLinks = (filterType = 'pending', ids = null, format = 'json') => {
  const config = {
    params: { filterType, ids, format }
  };

  // Se for Excel, precisamos receber como blob
  if (format === 'excel') {
    config.responseType = 'blob';
  }

  return api.get('/pending-domains/export-links', config);
};

export const importAudienceJson = (payload) => {
  if (payload instanceof FormData) {
    return api.post('/pending-domains/import-audience', payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
  return api.post('/pending-domains/import-audience', payload);
};

// Lista arquivos JSON do ScrapAudience
export const listScraperFiles = () =>
  api.get('/pending-domains/scraper-files');

// Importa arquivo específico do ScrapAudience
export const importScraperFile = (filename) =>
  api.post('/pending-domains/import-scraper-file', { filename });

// Funções para configuração de fallback estatístico
export const getFallbackConfig = () => api.get('/pending-domains/fallback-config');
export const updateFallbackConfig = (data) => api.put('/pending-domains/fallback-config', data);
export const recalculateFallback = () => api.post('/pending-domains/fallback-config/recalculate');

// Funções para recálculo de prioridades baseado em tier/uso
export const recalculatePriorities = () => api.post('/pending-domains/recalculate-priorities', {}, { timeout: 120000 });
export const getPriorityStats = () => api.get('/pending-domains/priority-stats');
export const getPendingDomainsStats = () => api.get('/pending-domains/stats');

// Funções para Views Inteligentes de Domínios Pendentes
export const getViews = () => api.get('/pending-domains/views');
export const getViewData = (viewId, params) => api.get(`/pending-domains/views/${viewId}`, { params });
export const getViewCount = (viewId) => api.get(`/pending-domains/views/${viewId}/count`);
export const getAllViewCounts = () => api.get('/pending-domains/views-counts');
export const getViewsStats = () => api.get('/pending-domains/views-stats');
export const getRecommendedView = () => api.get('/pending-domains/views/recommended');

// Importação em massa de URLs
export const importUrls = (urls) => api.post('/pending-domains/import-urls', { urls });

// Funções para SimilarWeb Scraper integrado
export const similarWebOpenLogin = () => api.post('/similarweb/open-login', {}, { timeout: 120000 }); // 2 min para abrir Chrome
export const similarWebCheckSession = () => api.get('/similarweb/check-session');
export const similarWebScrape = (domains) => api.post('/similarweb/scrape', { domains });
export const similarWebScrapeSync = (domains) => api.post('/similarweb/scrape-sync', { domains }, { timeout: 600000 });
export const similarWebGetStatus = () => api.get('/similarweb/status');
export const similarWebStop = () => api.post('/similarweb/stop');
export const similarWebClearCache = () => api.post('/similarweb/clear-cache');
export const similarWebResetProfile = () => api.post('/similarweb/reset-profile');

// Funções para HypeStat Scraper (multi-fonte: HypeStat + SEMrush + SimilarWeb)
export const hypeStatOpenBrowser = () => api.post('/hypestat/open-browser');
export const hypeStatCloseBrowser = () => api.post('/hypestat/close-browser');
export const hypeStatCheckStatus = () => api.get('/hypestat/status');
export const hypeStatScrape = (domains) => api.post('/hypestat/scrape', { domains });
export const hypeStatScrapeSync = (domains) => api.post('/hypestat/scrape-sync', { domains }, { timeout: 600000 });
export const hypeStatGetProgress = () => api.get('/hypestat/progress');
export const hypeStatStop = () => api.post('/hypestat/stop');
export const hypeStatScrapeSingle = (domain) => api.post('/hypestat/scrape-single', { domain });

// Funções para Pinterest Scraper
export const pinterestProcess = (url) => api.post('/pinterest/process', { url }, { timeout: 120000 });
export const pinterestProcessBatch = (urls) => api.post('/pinterest/process-batch', { urls }, { timeout: 300000 });
export const pinterestGetInfo = (url) => api.get('/pinterest/info', { params: { url } });

// Funções para Análise de Sentimento (Gemini AI)
export const getSentimentStatus = () => api.get('/sentiment/status');
export const getSentimentStats = () => api.get('/sentiment/stats');
export const analyzeSentiment = (articleId, save = true, options = {}) =>
  api.post(`/sentiment/analyze/${articleId}`, { save, ...options });
export const analyzeSentimentBatch = (articleIds, save = true, delayMs = 500, options = {}) =>
  api.post('/sentiment/analyze-batch', { articleIds, save, delayMs, ...options }, { timeout: 300000 });
export const analyzeSentimentPending = (limit = 20, delayMs = 1000, options = {}) =>
  api.post('/sentiment/analyze-pending', { limit, delayMs, ...options }, { timeout: 300000 });

// Funções para Configurações do Sistema
export const getSystemConfig = () => api.get('/system-config');
export const updateSystemConfig = (config) => api.put('/system-config', config);
export const getSystemFeatures = () => api.get('/system-config/features');

// Funções para Scheduler de Enriquecimento
export const getSchedulerStatus = () => api.get('/system-config/scheduler/status');
export const runScheduler = () => api.post('/system-config/scheduler/run', {}, { timeout: 300000 });
export const updateSchedulerConfig = (config) => api.put('/system-config/scheduler', config);
export const resetSchedulerStats = () => api.post('/system-config/scheduler/reset-stats');

// Funções para Enriquecimento de Domínios com Gemini AI
export const enrichDomain = (id) => api.post(`/pending-domains/${id}/enrich`);
export const enrichDomainsBatch = (domainIds) =>
  api.post('/pending-domains/enrich-batch', { domainIds }, { timeout: 300000 });

// Deduplicacao de PendingDomains (dominios com registrableDomain identico)
export const dedupPendingDomains = (dryRun = false) =>
  api.post('/pending-domains/dedup', { dryRun }, { timeout: 60000 });

// Remover PendingDomain permanentemente
export const deletePendingDomain = (id) => api.delete(`/pending-domains/${id}`);

// Enriquecimento em massa com Gemini (fire-and-forget)
export const enrichPendingDomainsBatch = (domainIds) =>
  api.post('/pending-domains/enrich-batch', { domainIds }, { timeout: 15000 });

// Progresso do enriquecimento em lote
export const getEnrichProgress = () => api.get('/pending-domains/enrich-progress');

export default api;
